import { useEffect, useRef, useState } from 'react'
import { ActionType, type Chart, type Crosshair } from 'klinecharts'
import type { EventBus } from '../core/EventBus'
import type { Period } from '../utils/resolution'
import { isIntradayPeriod } from '../utils/resolution'
import { CHART_FONT_FAMILY } from '../utils/chartColors'

export interface CrosshairTimeLabelProps {
  chart: Chart | null
  eventBus: EventBus | null
  /** Drives intraday vs. date-only formatting of the hovered bar's timestamp. */
  period: Period
  /** IANA timezone the chart renders in (matches the x-axis tick labels). */
  timezone: string
  /** Pill background — mirrors klinecharts' crosshair label background. */
  crosshairLabelBg: string
}

/**
 * Format the hovered bar's timestamp for the floating time pill. Intraday
 * periods (minute / hour) show `MMM d, HH:mm`; day and above show
 * `MMM d, yyyy`. Rendered in the chart's timezone so it matches the axis ticks.
 */
function formatCrosshairTime(timestamp: number, period: Period, timezone: string): string {
  const intraday = isIntradayPeriod(period)
  const opts: Intl.DateTimeFormatOptions = intraday
    ? {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone,
      }
    : { month: 'short', day: 'numeric', year: 'numeric', timeZone: timezone }
  try {
    return new Intl.DateTimeFormat('en-US', opts).format(new Date(timestamp))
  } catch {
    // Invalid timezone string — fall back to the runtime default.
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: undefined }).format(
      new Date(timestamp),
    )
  }
}

/**
 * Floating crosshair TIME label for the "hide price & time scales" mode. With
 * the x-axis strip collapsed to 1px (see ChartManager.setHideAxisScale) the
 * native crosshair time label — which klinecharts draws inside that strip — is
 * clipped away. This React overlay replaces it: a pill pinned to the chart-area
 * bottom, centered on the crosshair x and clamped to the container width so it
 * never overflows or collides with the bottom-right settings gear. Mirrors the
 * price-side behavior (yAxis inside:true overlays the price label at the edge).
 */
export function CrosshairTimeLabel({
  chart,
  eventBus,
  period,
  timezone,
  crosshairLabelBg,
}: CrosshairTimeLabelProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [label, setLabel] = useState<{ x: number; text: string; width: number } | null>(null)

  useEffect(() => {
    if (!chart) return

    const onCrosshair = (crosshair: Crosshair) => {
      const bar = crosshair.kLineData
      const x = crosshair.x ?? crosshair.realX
      // No hovered bar (crosshair off a data point) → hide the pill.
      if (!bar || x == null) {
        setLabel(null)
        return
      }
      setLabel({
        x,
        text: formatCrosshairTime(bar.timestamp, period, timezone),
        width: wrapRef.current?.clientWidth ?? 0,
      })
    }

    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    // Snap off when the cursor leaves the chart area (EdgeChart emits this on
    // the chart's mouseleave — same signal OhlcHeader uses to reset).
    const unsubLeave = eventBus?.on('crosshair:leave', () => setLabel(null))

    return () => {
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
      unsubLeave?.()
    }
  }, [chart, eventBus, period, timezone])

  // Clamp the centered pill within the container (measured at the crosshair
  // event): never past ~30px from the left edge, and stop short of the
  // bottom-right gear zone (width - 40).
  let left = label?.x ?? 0
  if (label) {
    const maxX = Math.max(30, label.width - 40)
    left = Math.min(Math.max(left, 30), maxX)
  }

  // Wrapper is always mounted (zero-height, full width) so its clientWidth is
  // measurable on the first crosshair event — the pill renders conditionally.
  return (
    <div
      ref={wrapRef}
      data-testid="crosshair-time-label-wrap"
      className="pointer-events-none absolute inset-x-0 bottom-0.5 z-20"
    >
      {label && (
        <div
          data-testid="crosshair-time-label"
          className="absolute bottom-0 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[11px] leading-none text-white"
          style={{
            left,
            transform: 'translateX(-50%)',
            background: crosshairLabelBg,
            fontFamily: CHART_FONT_FAMILY,
          }}
        >
          {label.text}
        </div>
      )}
    </div>
  )
}
